export const ImagePrompt = `Extract the following details from the given image and return a valid JSON object (without any markdown formatting):
        1. **Gender Detection**
        - Must be one of: "men", "women", "kid", "boy", or "girl".
        - If detected as "male", return "men".
        - If detected as "female", return "women".
        - If detected as "child", determine "boy" or "girl" based on facial features and clothing style.
      
        2. **Outfit Attributes**
        - "primary_color": The dominant color of the outfit.
        - "secondary_color": A secondary color if present, otherwise null.
        - "brand": Extract the brand if visible. If unknown, return null.
        - "category": Identify the type of clothing (e.g., "T-shirt", "jeans", "jacket", "sneakers").
        - "material": Extract the fabric type if visible (e.g., "cotton", "denim", "leather", "polyester").
      
        3. **Extracted Outfit (Searchable Format)**
        - List **each detected clothing item** dynamically.
        - Format for each item: "<clothing_item>: <gender> <primary_color> <secondary_color> <brand> <material> <category>"
        - If any value is null, **skip it instead of writing "null"**.
        - Example:
          "extracted_outfit": {
            "T-Shirt": "<gender> <primary_color> <secondary_color> <brand> <material> <category>",
            "Joggers": "<gender> <primary_color> <brand> <material> <category>"
          }
      
        4. **Suggested Outfit (Detailed Styling)**
        - Provide recommendations for styling based on detected outfit.
        - Each suggested item must include:
          - "search_term": Suggestive Search Term (e.g "Rugged Blue Jeans", "White Sneakers").
          - "category": T-shirt, Jeans, jacket, sneakers, etc.
          - "type_of_wear": Suggested occasions (e.g., "casual wear", "formal wear", "party wear").
          - "description": A suggestive style description in 50 words.
          - "color": Suggest the color (e.g "blue", "black").
          - "material": Suggest the material (e.g "cotton", "denim").
        - Example:
          "bottomwear": {
            "search_term": "Rugged Blue Jeans",
            "category": "Denim Jeans", 
            "type_of_wear": "casual wear",
            "description": "Blue jeans with wrinkles for a rugged look",
            "color": Suggest the color (e.g "blue", "black"),
            "material": Suggest the material (e.g "cotton", "denim")
          },
          "footwear": {
            "search_term": "White Sneakers",
            "category": "Sneakers",
            "description": "White sneakers with a minimalist design",
            "type_of_wear": "casual wear, streetwear",
            "color": Suggest the color (e.g "white", "black"),
            "material": Suggest the material (e.g "leather", "canvas")
          }
      
        5. **Image Data Extraction**
        - **Detected Items**: Provide a list of extracted items with bounding box coordinates, confidence score, and relative position ("upper_body", "lower_body", "accessory_zone").
        - **Brand Logo Detection**: Indicate if a brand logo is detected (true or false).
      
        **STRICT RULES:**
        - Return a valid JSON object without any markdown formatting.
        - Do NOT add markdown formatting in the output.
        - DO NOT return "male" or "female", always return "men" or "women".
        - Ensure JSON format is valid and contains no extra commas.
        - Return null for unknown values instead of "Unknown".
        - extracted_outfit should be a sensible searchable format that can be used to search for similar items.
      
        Return the output in the following valid JSON format:
      
        {
          "gender": "<men | women | kid | boy | girl>",
          "primary_color": "<string>",
          "secondary_color": "<string | null>",
          "brand": "<string | null>",
          "category": "<string | null>",
          "material": "<string | null>",
          "extracted_outfit": {
            "<clothing_item>": "<formatted string>"
          },
          "suggested_outfit": {
            "bottomwear": {
            "search_term": "<string | null>",
            "category": "<string | null>", 
            "type_of_wear": "<string | null>",
            "description": "<string | null>",
            "color": "<string | null>",
            "material": "<string | null>"
            },
            "footwear": {
            "search_term": "<string | null>",
            "category": "<string | null>", 
            "type_of_wear": "<string | null>",
            "description": "<string | null>",
            "color": "<string | null>",
            "material": "<string | null>"
            },
            "accessory": {
            "search_term": "<string | null>",
            "category": "<string | null>", 
            "type_of_wear": "<string | null>",
            "description": "<string | null>",
            "color": "<string | null>",
            "material": "<string | null>"
            }
          },
          "image_data_extraction": {
            "extracted_items": [
              {
                "label": "<string>",
                "bounding_box": {
                  "x": <number>,
                  "y": <number>,
                  "width": <number>,
                  "height": <number>
                },
                "relative_position": "<upper_body | lower_body | accessory_zone>",
                "confidence": <number>
              }
            ],
            "logo_detected": <true | false>
          }
        }`;

// const result3String = await session.prompt([
//   `Extract the following details from the given image and return a valid JSON object (without markdown formatting):

//   1. **Gender Detection**
//   - Must be one of: "men", "women", "kid", "boy", or "girl".
//   - If detected as "male", return "men".
//   - If detected as "female", return "women".
//   - If detected as "child", determine "boy" or "girl" based on facial features and clothing style.

//   2. **Outfit Attributes**
//   - "primary_color": The dominant color of the outfit.
//   - "secondary_color": A secondary color if present, otherwise null.
//   - "brand": Extract the brand if visible. If unknown, return null.
//   - "category": Identify the type of clothing (e.g., "T-shirt", "jeans", "jacket", "sneakers").
//   - "material": Extract the fabric type if visible (e.g., "cotton", "denim", "leather").

//   3. **Extracted Outfit (Detected Items)**
//   - List detected clothing items dynamically in "extracted_outfit".
//   - The key should be the clothing category (e.g., "T-shirt", "jeans") and the value should describe it (e.g., "Navy Puma T-shirt").

//   4. **Suggested Outfit (Complementary Items)**
//   - "bottomwear": Suggested pants, jeans, skirts, etc., that match the detected outfit.
//   - "footwear": Suggested shoes or sandals.
//   - "accessory": Suggested complementary accessories (e.g., sunglasses, watch, bracelet).
//   - If no relevant suggestion is available, return null.

//   5. **Image Data Extraction**
//   - **Detected Items**: Provide a list of extracted items with bounding box coordinates, confidence score, and relative position ("upper_body", "lower_body", "accessory_zone").
//   - **Brand Logo Detection**: Indicate if a brand logo is detected (true or false).

//   **STRICT RULES:**
//   - DO NOT return "male" or "female", always return "men" or "women".
//   - Ensure JSON format is valid and contains no extra commas.
//   - Return null for unknown values instead of "Unknown".

//   Return the output in the following valid JSON format:

//   {
//     "gender": "<men | women | kid | boy | girl>",
//     "primary_color": "<string>",
//     "secondary_color": "<string | null>",
//     "brand": "<string | null>",
//     "category": "<string | null>",
//     "material": "<string | null>",
//     "extracted_outfit": {
//       "<clothing_item>": "<description>"
//     },
//     "suggested_outfit": {
//       "bottomwear": "<string | null>",
//       "footwear": "<string | null>",
//       "accessory": "<string | null>"
//     },
//     "image_data_extraction": {
//       "extracted_items": [
//         {
//           "label": "<string>",
//           "bounding_box": {
//             "x": <number>,
//             "y": <number>,
//             "width": <number>,
//             "height": <number>
//           },
//           "relative_position": "<upper_body | lower_body | accessory_zone>",
//           "confidence": <number>
//         }
//       ],
//       "logo_detected": <true | false>
//     }
//   }`,
//   { type: 'image', content: img },
// ]);
